import { useCallback, useEffect, useRef, useState } from "react";
import {
  finishClassroomHandoverSetup,
  getClassroomHandoverChildren,
} from "../services/APIClient";
import { getClassroomHandoverIdempotencyKey } from "../utils/classroomHandoverIdempotency";

const ATTRIBUTE_KEYS = ["eal", "speechAndLanguage", "asd", "gdd"];

const childId = (child) => child.id || child.studentId || child.studentid;
const childName = (child) => child.name || child.studentName || child.studentname || "";

const normalizeHandoverChild = (child) => ({
  ...child,
  id: childId(child),
  name: childName(child),
  notes: child.notes || "",
  eal: Boolean(child.eal || child.esl),
  speechAndLanguage: Boolean(child.speechAndLanguage || child.sen),
  asd: Boolean(child.asd),
  gdd: Boolean(child.gdd),
});

const normalizeHandoverChildren = (response) => {
  const children = Array.isArray(response)
    ? response
    : response?.children ?? response?.students ?? response?.roster;
  if (!Array.isArray(children)) {
    throw new Error("The child roster response was invalid. Please try again.");
  }
  return children.map(normalizeHandoverChild);
};

const copy = (value) => JSON.parse(JSON.stringify(value));
const equalValue = (left, right) => left === right;
const updateChild = (state, childId, update) => ({
  ...state,
  children: state.children.map((child) => child.id === childId ? update(child) : child),
});

export const createChildReviewState = (children, draft = null) => {
  const baseChildren = children.map(copy);
  if (!draft?.children) {
    return { baseChildren, children: children.map(copy), nextTemporaryId: -1 };
  }
  return {
    baseChildren,
    children: draft.children.map(copy),
    nextTemporaryId: draft.nextTemporaryId || -1,
  };
};

export const childReviewReducer = (state, action) => {
  switch (action.type) {
    case "replace":
      return createChildReviewState(action.children, action.draft);
    case "add":
      return {
        ...state,
        nextTemporaryId: state.nextTemporaryId - 1,
        children: [...state.children, {
          id: `new-${Math.abs(state.nextTemporaryId)}`,
          clientId: `new-${Math.abs(state.nextTemporaryId)}`,
          name: action.name.trim(),
          notes: "",
          eal: false,
          speechAndLanguage: false,
          asd: false,
          gdd: false,
          isNew: true,
        }],
      };
    case "update":
      return updateChild(state, action.id, (child) => ({
        ...child,
        [action.field]: action.value,
      }));
    case "remove":
      return updateChild(state, action.id, (child) => ({
        ...child,
        removal: {
          reason: action.reason,
          ...(action.reason === "duplicate" ? { canonicalStudentId: action.canonicalStudentId } : {}),
          ...(action.reason === "other" ? { administrativeNote: action.administrativeNote } : {}),
        },
      }));
    case "undoRemove":
      return updateChild(state, action.id, (child) => {
        const nextChild = { ...child };
        delete nextChild.removal;
        return nextChild;
      });
    default:
      return state;
  }
};

const changedFields = (child, original) => ATTRIBUTE_KEYS.concat(["name", "notes"])
  .filter((field) => !equalValue(child[field] || "", original[field] || ""))
  .reduce((changes, field) => ({ ...changes, [field]: child[field] }), {});

export const getChildNameError = (children, name, currentId = null) => {
  const normalized = name.trim().toLocaleLowerCase();
  if (!normalized) return "Enter the child’s full name.";
  const duplicate = children.some((child) =>
    !child.removal
    && child.id !== currentId
    && child.name.trim().toLocaleLowerCase() === normalized
  );
  return duplicate ? "A child with that name is already in this class." : null;
};

const removalOperationBuilders = {
  repeating: (child) => ({ studentId: child.id, reason: child.removal.reason }),
  no_longer_in_school: (child) => ({ studentId: child.id, reason: child.removal.reason }),
  duplicate: (child, { originalById, currentById }) => {
    const canonicalStudentId = child.removal.canonicalStudentId?.trim();
    if (!canonicalStudentId || canonicalStudentId === child.id) {
      throw new Error("Choose another child as the canonical duplicate.");
    }
    const canonicalChild = currentById.get(canonicalStudentId);
    if (!originalById.has(canonicalStudentId) || !canonicalChild || canonicalChild.removal) {
      throw new Error("Choose an active existing child as the canonical duplicate.");
    }
    return { studentId: child.id, reason: child.removal.reason, canonicalStudentId };
  },
  other: (child) => {
    const administrativeNote = child.removal.administrativeNote?.trim() || "";
    if (administrativeNote.length < 3 || administrativeNote.length > 500) {
      throw new Error("Enter an administrative note between 3 and 500 characters.");
    }
    return { studentId: child.id, reason: child.removal.reason, administrativeNote };
  },
};

const buildRemovalOperation = (child, context) => {
  const build = removalOperationBuilders[child.removal.reason];
  if (!build) throw new Error("Choose a supported removal reason.");
  return build(child, context);
};

export const buildHandoverChildChangeSet = (state) => {
  const originalById = new Map(state.baseChildren.map((child) => [child.id, child]));
  const currentById = new Map(state.children.map((child) => [child.id, child]));
  const added = [];
  const edited = [];
  const removed = [];

  state.children.forEach((child) => {
    const original = originalById.get(child.id);
    if (child.removal) {
      if (original) removed.push(buildRemovalOperation(child, { originalById, currentById }));
      return;
    }
    if (!original) {
      const { id, clientId, isNew, removal, ...newChild } = child;
      added.push({
        clientId: clientId || id,
        name: newChild.name,
        notes: newChild.notes,
        esl: newChild.eal,
        sen: newChild.speechAndLanguage,
        asd: newChild.asd,
        gdd: newChild.gdd,
      });
      return;
    }
    const changes = changedFields(child, original);
    if (Object.keys(changes).length) {
      const apiChanges = Object.entries(changes).reduce((result, [field, value]) => ({
        ...result,
        [{ eal: "esl", speechAndLanguage: "sen" }[field] || field]: value,
      }), {});
      edited.push({ studentId: child.id, changes: apiChanges });
    }
  });
  return { added, edited, removed };
};

export const getFinishIdempotencyKey = (classId, version, changes) =>
  getClassroomHandoverIdempotencyKey(classId, "finish", version, changes);

const statusFromError = (error) => {
  if (error?.response?.status === 403) return "forbidden";
  if (error?.response?.status === 404) return "not_found";
  if (error?.response?.status === 409) return "conflict";
  if (error?.response?.status === 422 || error?.response?.status === 400) return "validation";
  return "error";
};

export const reconcileChildReviewState = (state, latestChildren) => {
  const latest = latestChildren.map(copy);
  const priorBaseById = new Map(state.baseChildren.map((child) => [child.id, child]));
  const localById = new Map(state.children.map((child) => [child.id, child]));
  const merged = latest.map((serverChild) => {
    const oldBase = priorBaseById.get(serverChild.id);
    const local = localById.get(serverChild.id);
    if (!oldBase || !local) return serverChild;
    const changes = changedFields(local, oldBase);
    return { ...serverChild, ...changes, ...(local.removal ? { removal: local.removal } : {}) };
  });
  state.children.filter((child) => !priorBaseById.has(child.id)).forEach((child) => merged.push(copy(child)));
  return { baseChildren: latest, children: merged, nextTemporaryId: state.nextTemporaryId };
};

export default function useClassroomHandoverChildren({ schoolId, classId, stagedDraft }) {
  const [entry, setEntry] = useState(null);
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const attemptRef = useRef(null);
  const finishPendingRef = useRef(false);

  const load = useCallback(async () => {
    if (!schoolId || !classId) return null;
    setStatus("loading");
    setError(null);
    try {
      const response = await getClassroomHandoverChildren(schoolId, classId);
      const children = normalizeHandoverChildren(response);
      setEntry(response);
      setState((current) => {
        if (current) return reconcileChildReviewState(current, children);
        if (stagedDraft?.children && Array.isArray(stagedDraft.baseChildren)) {
          return reconcileChildReviewState(stagedDraft, children);
        }
        return createChildReviewState(children, stagedDraft);
      });
      setStatus(response?.setupStatus === "complete" ? "complete" : "ready");
      return response;
    } catch (nextError) {
      setError(nextError);
      setStatus(statusFromError(nextError));
      throw nextError;
    }
  }, [classId, schoolId, stagedDraft]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const dispatch = useCallback((action) => setState((current) => childReviewReducer(current, action)), []);

  const finish = useCallback(async () => {
    if (!entry || !state || status === "finishing" || finishPendingRef.current) return null;
    let changes;
    try {
      changes = buildHandoverChildChangeSet(state);
    } catch (nextError) {
      setError(nextError);
      setStatus("validation");
      throw nextError;
    }
    const signature = JSON.stringify({ expectedVersion: entry.version, changes });
    if (attemptRef.current?.signature !== signature) {
      attemptRef.current = {
        signature,
        key: getFinishIdempotencyKey(classId, entry.version, changes),
      };
    }
    finishPendingRef.current = true;
    setStatus("finishing");
    setError(null);
    try {
      const result = await finishClassroomHandoverSetup(schoolId, classId, {
        expectedVersion: entry.version,
        changes,
        idempotencyKey: attemptRef.current.key,
      });
      setStatus("complete");
      attemptRef.current = null;
      return result;
    } catch (nextError) {
      setError(nextError);
      const nextStatus = statusFromError(nextError);
      if (nextStatus === "conflict") {
        try {
          const latest = await getClassroomHandoverChildren(schoolId, classId);
          const latestChildren = normalizeHandoverChildren(latest);
          setEntry(latest);
          setState((current) => reconcileChildReviewState(current, latestChildren));
          if (latest?.setupStatus === "complete") {
            setError(null);
            setStatus("complete");
            attemptRef.current = null;
            return latest;
          }
        } catch (_) {
          // Keep the staged state even if the authoritative recovery read fails.
        }
      }
      setStatus(nextStatus);
      throw nextError;
    } finally {
      finishPendingRef.current = false;
    }
  }, [classId, entry, schoolId, state, status]);

  return { entry, state, status, error, load, dispatch, finish };
}
